import type { Component, Editor } from 'grapesjs';
import type { DeviceId, EditorAdapter, EditorEventName } from '../types';

const deviceMap: Record<DeviceId, string> = {
  desktop: 'Desktop',
  laptop: 'Laptop',
  tablet: 'Tablet',
  mobile: 'Mobile',
};
export class WebKilnEditorAdapter implements EditorAdapter {
  private editor: Editor | null = null;
  private readonly listeners = new Map<EditorEventName, Set<(...args: unknown[]) => void>>();
  private isExporting = false;
  private cachedStyles = '';

  constructor(private readonly sourceContainer: HTMLElement) { }

  async initialize(): Promise<void> {
    if (this.editor) return;
    const { default: webkilnEditorRuntime } = await import('grapesjs');
    const initialHtml = this.sourceContainer.innerHTML;
    const stylesheetHrefs = Array.from(
      document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
    ).map((link) => link.href);
    const styles = await this.collectStyles();
    this.cachedStyles = styles;
    this.sourceContainer.innerHTML = '';
    this.sourceContainer.classList.add('webkiln-mounted');
    this.editor = webkilnEditorRuntime.init({
      container: this.sourceContainer,
      height: '100%',
      headless: false,
      fromElement: false,
      storageManager: false,
      selectorManager: { componentFirst: true },
      deviceManager: {
        devices: [
          { id: 'Desktop', name: 'Desktop', width: '1200px' },
          { id: 'Laptop', name: 'Laptop', width: '1024px' },
          { id: 'Tablet', name: 'Tablet', width: '768px' },
          { id: 'Mobile', name: 'Mobile', width: '390px' },
        ],
      },
      canvas: { styles: stylesheetHrefs },
      components: `<div class="site-canvas desktop">${initialHtml}</div>`,
      panels: { defaults: [] },
      blockManager: { blocks: [] },
    });
    this.setCanvasFrameAccessibility();
    this.editor.addStyle(styles);
    this.editor.on('load', () => {
      this.setCanvasFrameAccessibility();
      this.editor?.Canvas.getBody()?.classList.add('webkiln-canvas-body');
      this.injectFrameStyles(this.cachedStyles);
      this.emit('load');
    });
    this.editor.on('canvas:frame:load', () => {
      this.setCanvasFrameAccessibility();
      this.editor?.Canvas.getBody()?.classList.add('webkiln-canvas-body');
      this.injectFrameStyles(this.cachedStyles);
    });
    this.editor.on('frame:load', () => {
      this.setCanvasFrameAccessibility();
      this.editor?.Canvas.getBody()?.classList.add('webkiln-canvas-body');
      this.injectFrameStyles(this.cachedStyles);
    });
    this.injectFrameStyles(styles);
    this.editor.on('update', () => this.emit('update'));
    this.editor.on('component:selected', (component) => this.emit('select', component));
    this.editor.on('component:deselected', () => this.emit('select', null));
    this.editor.on('component:update', (component) => this.emit('update', component));
    this.editor.on('component:styleUpdate', (component) => this.emit('update', component));
    this.editor.on('component:add', (component) => this.emit('update', component));
    this.editor.on('component:remove', (component) => this.emit('update', component));
    this.editor.on('component:move', (component) => this.emit('update', component));
    this.setDevice('desktop');
  }

  private setCanvasFrameAccessibility(): void {
    const frame = this.sourceContainer.querySelector<HTMLIFrameElement>('iframe.gjs-frame');
    if (frame) {
      frame.title = 'WebKiln website canvas preview';
      frame.setAttribute('scrolling', 'yes');
    }
  }

  private injectFrameStyles(styles: string = this.cachedStyles): void {
    const frameDocument = this.editor?.Canvas.getDocument();
    if (!frameDocument) return;
    if (!frameDocument.head.querySelector('link[data-webkiln-fonts]')) {
      const fontPreconnect1 = frameDocument.createElement('link');
      fontPreconnect1.rel = 'preconnect';
      fontPreconnect1.href = 'https://fonts.googleapis.com';
      frameDocument.head.appendChild(fontPreconnect1);

      const fontPreconnect2 = frameDocument.createElement('link');
      fontPreconnect2.rel = 'preconnect';
      fontPreconnect2.href = 'https://fonts.gstatic.com';
      fontPreconnect2.crossOrigin = 'anonymous';
      frameDocument.head.appendChild(fontPreconnect2);

      const fontLink = frameDocument.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href =
        'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@500;600;700&display=swap';
      fontLink.dataset.webkilnFonts = 'true';
      frameDocument.head.appendChild(fontLink);
    }
    const style =
      frameDocument.head.querySelector<HTMLStyleElement>('[data-webkiln-styles]') ??
      frameDocument.createElement('style');
    style.dataset.webkilnStyles = 'true';
    style.textContent = `${styles}\nhtml,body{overflow:auto!important}body{margin:0;background:#0b0d0f!important;color:#f4f5f3!important}`;
    frameDocument.head.appendChild(style);
  }

  private async collectStyles(): Promise<string> {
    const linkedStyles = await Promise.all(
      Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')).map(
        async (link) => {
          try {
            return await fetch(link.href).then((response) => response.text());
          } catch {
            return '';
          }
        },
      ),
    );
    const inlineStyles = Array.from(document.styleSheets)
      .flatMap((sheet) => {
        try {
          return Array.from(sheet.cssRules).map((rule) => rule.cssText);
        } catch {
          return [];
        }
      })
      .join('\n');
    return `${linkedStyles.join('\n')}\n${inlineStyles}`;
  }

  private requireEditor(): Editor {
    if (!this.editor) throw new Error('WebKiln editor adapter has not been initialized');
    return this.editor;
  }

  private emit(event: EditorEventName, ...args: unknown[]): void {
    if (this.isExporting && event === 'update') return;
    this.listeners.get(event)?.forEach((callback) => callback(...args));
  }

  getEditor(): Editor {
    return this.requireEditor();
  }

  getRoot(): Component | null {
    return this.requireEditor().getWrapper() ?? null;
  }

  getChildren(component?: Component): Component[] {
    const target = component ?? this.getRoot();
    return target ? target.components().models : [];
  }

  loadProjectData(data: unknown): void {
    const editor = this.requireEditor();
    const legacy = data as { components?: unknown; styles?: unknown; pages?: unknown } | null;
    if (legacy?.components !== undefined && !Array.isArray(legacy.pages)) {
      editor.loadProjectData({
        pages: [{ frames: [{ component: { type: 'wrapper', components: [] } }] }],
        styles: Array.isArray(legacy.styles) ? legacy.styles : [],
      });
      editor.setComponents(legacy.components as Parameters<Editor['setComponents']>[0]);
      if (typeof legacy.styles === 'string') editor.addStyle(legacy.styles);
    } else {
      editor.loadProjectData(data as Parameters<Editor['loadProjectData']>[0]);
    }
    this.ensureCanvasRoot();
    this.setCanvasFrameAccessibility();
    this.editor?.Canvas.getBody()?.classList.add('webkiln-canvas-body');
    this.injectFrameStyles(this.cachedStyles);
    window.requestAnimationFrame(() => {
      this.injectFrameStyles(this.cachedStyles);
    });
    window.setTimeout(() => {
      this.injectFrameStyles(this.cachedStyles);
    }, 50);
  }

  private ensureCanvasRoot(): void {
    const root = this.requireEditor().getWrapper();
    if (!root) return;
    const first = root.components().models[0];
    const firstClass = String(
      (first?.getAttributes() as Record<string, unknown> | undefined)?.class ?? '',
    );
    if (firstClass.split(/\s+/).includes('site-canvas')) return;
    const wrapper = root.append({
      tagName: 'div',
      attributes: { class: 'site-canvas desktop' },
    })[0];
    if (!wrapper) return;
    root
      .components()
      .models.slice(0, -1)
      .forEach((component) => wrapper.append(component));
  }

  flushEditing(): void {
    if (!this.editor) return;
    try {
      const em = (this.editor as unknown as { em?: { getEditing?: () => Component | undefined } }).em;
      const editingComp =
        em?.getEditing?.() ??
        (this.editor as unknown as { getEditing?: () => Component | undefined }).getEditing?.() ??
        null;
      if (editingComp) {
        const view = editingComp.getView?.() as { disableEditing?: () => void } | undefined;
        view?.disableEditing?.();
      }
    } catch {
      return;
    }
  }

  exportProjectData(): unknown {
    if (this.isExporting) {
      return this.requireEditor().getProjectData();
    }
    this.isExporting = true;
    try {
      const editor = this.requireEditor();
      const em = (editor as unknown as { em?: { getEditing?: () => Component | undefined } }).em;
      const editingComp =
        em?.getEditing?.() ??
        (editor as unknown as { getEditing?: () => Component | undefined }).getEditing?.() ??
        null;

      const editingId = editingComp?.getId?.() ?? null;
      const editingEl = (editingComp?.getView?.()?.el as HTMLElement | null) ?? null;
      const liveHtml = editingEl?.innerHTML ?? null;

      const origEmGetEditing = em?.getEditing;
      const origEditorGetEditing = (editor as unknown as { getEditing?: () => unknown }).getEditing;

      if (editingComp) {
        if (em && typeof origEmGetEditing === 'function') {
          em.getEditing = () => undefined;
        }
        if (typeof origEditorGetEditing === 'function') {
          (editor as unknown as { getEditing: () => unknown }).getEditing = () => undefined;
        }
      }

      let data: unknown;
      try {
        data = editor.getProjectData();
      } finally {
        if (em && origEmGetEditing) {
          em.getEditing = origEmGetEditing;
        }
        if (origEditorGetEditing) {
          (editor as unknown as { getEditing: () => unknown }).getEditing = origEditorGetEditing;
        }
      }

      if (editingId && liveHtml !== null && data && typeof data === 'object') {
        this.patchProjectDataContent(data, editingId, liveHtml);
      }

      return data;
    } finally {
      this.isExporting = false;
    }
  }

  private patchProjectDataContent(node: unknown, targetId: string, html: string): boolean {
    if (!node || typeof node !== 'object') return false;
    const record = node as Record<string, unknown>;
    const attrs = (record.attributes as Record<string, unknown> | undefined) ?? {};
    if (record.id === targetId || attrs.id === targetId) {
      if (typeof record.content === 'string' || record.type === 'text') {
        record.content = html;
      }
      if (Array.isArray(record.components)) {
        record.components = [{ type: 'textnode', content: html }];
      }
      return true;
    }
    for (const key of ['pages', 'frames', 'components']) {
      const list = record[key];
      if (Array.isArray(list)) {
        for (const item of list) {
          if (this.patchProjectDataContent(item, targetId, html)) return true;
        }
      }
    }
    if (record.component && this.patchProjectDataContent(record.component, targetId, html)) {
      return true;
    }
    return false;
  }

  addComponent(component: unknown, target?: Component): unknown {
    const parent = target ?? this.getRoot();
    return parent?.append(component as Parameters<Component['append']>[0]);
  }

  selectComponent(component: unknown): void {
    this.requireEditor().select(component as Parameters<Editor['select']>[0]);
  }

  deleteComponent(component?: unknown): void {
    const selected = (component as Component | undefined) ?? this.requireEditor().getSelected();
    selected?.remove();
  }

  duplicateComponent(component?: unknown): unknown {
    const selected = (component as Component | undefined) ?? this.requireEditor().getSelected();
    return selected?.clone();
  }

  moveComponent(component: unknown, before?: unknown): void {
    const target = before as Component | undefined;
    if (target) (component as Component).move(target, { at: 0 });
  }

  setComponentVisible(component: unknown, visible: boolean): void {
    (component as Component).set('visible', visible);
  }

  updateComponentTraits(component: unknown, traits: Record<string, unknown>): void {
    (component as Component).addAttributes(traits);
  }

  updateStyles(component: unknown, styles: Record<string, string>): void {
    (component as Component).setStyle(styles);
  }

  getComponentStyles(component?: unknown): Record<string, string> {
    const target = (component as Component | undefined) ?? this.getSelectedComponent();
    return (target?.getStyle?.() ?? {}) as Record<string, string>;
  }

  undo(): void {
    this.requireEditor().UndoManager.undo();
  }

  redo(): void {
    this.requireEditor().UndoManager.redo();
  }

  setDevice(device: DeviceId): void {
    const editor = this.requireEditor();
    editor.setDevice(deviceMap[device]);
    this.getRoot()?.setClass(`site-canvas ${device}`);
    this.sourceContainer.classList.remove('desktop', 'laptop', 'tablet', 'mobile');
    this.sourceContainer.classList.add(device);
    this.sourceContainer.style.width = '100%';
    this.emit('device', device);
  }

  setCustomDevice(id: string, width: number): void {
    const safeId = id.replace(/[^a-zA-Z0-9_-]/g, '-');
    const editor = this.requireEditor();
    const safeWidth = Math.max(320, Math.min(2400, width));
    const device =
      editor.DeviceManager.get(safeId) ??
      editor.DeviceManager.add({ id: safeId, name: safeId, width: `${safeWidth}px` });
    device.set('width', `${safeWidth}px`);
    editor.setDevice(safeId);
    const frame = this.sourceContainer.querySelector<HTMLElement>('.gjs-frame');
    if (frame) frame.style.maxWidth = `${safeWidth}px`;
    this.getRoot()?.setClass(`site-canvas ${safeId}`);
    this.sourceContainer.classList.remove('desktop', 'laptop', 'tablet', 'mobile');
    this.sourceContainer.classList.add(safeId);
    this.sourceContainer.style.width = '100%';
    this.emit('device', id as DeviceId);
  }

  getCanvasWidth(): number {
    return (
      Number.parseInt(
        this.requireEditor().DeviceManager.getSelected()?.get('width') ?? '1200',
        10,
      ) || 1200
    );
  }

  getSelectedComponent(): Component | null {
    return this.requireEditor().getSelected() ?? null;
  }

  getComponentId(component?: Component | null): string {
    const target = component ?? this.getSelectedComponent();
    if (!target) return '';
    return String(target.getId?.() ?? target.cid ?? '');
  }

  addResponsiveStyles(css: string): void {
    this.requireEditor().addStyle(css);
    this.injectFrameStyles(css);
  }

  setZoom(value: number): void {
    this.sourceContainer.style.setProperty(
      '--webkiln-frame-height',
      `${this.sourceContainer.clientHeight / Math.max(0.1, value / 100)}px`,
    );
    this.requireEditor().Canvas.fitViewport({ ignoreHeight: true, gap: 0, zoom: value });
  }

  setPreview(enabled: boolean): void {
    const editor = this.requireEditor();
    if (enabled) editor.runCommand('core:preview');
    else editor.stopCommand('core:preview');
    window.setTimeout(() => {
      const body = editor.Canvas.getBody();
      body?.classList.toggle('webkiln-preview', enabled);
      editor.Canvas.getDocument()
        ?.querySelectorAll<HTMLElement>('.canvas-toolbar, .selection-tag')
        .forEach((item) => {
          item.style.display = enabled ? 'none' : '';
        });
      this.injectFrameStyles(this.cachedStyles);
    }, 0);
  }

  getFrameDocument(): Document | null {
    return this.requireEditor().Canvas.getDocument();
  }

  subscribe(event: EditorEventName, callback: (...args: unknown[]) => void): () => void {
    const callbacks = this.listeners.get(event) ?? new Set();
    callbacks.add(callback);
    this.listeners.set(event, callbacks);
    return () => callbacks.delete(callback);
  }

  destroy(): void {
    this.editor?.destroy();
    this.editor = null;
    this.sourceContainer.classList.remove('webkiln-mounted');
    this.emit('destroy');
  }
}
